import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  BindVibeAppCredentialBodySchema,
  BindVibeAppCredentialPathVarsSchema,
  BindVibeAppCredentialResponseSchema,
  DeleteVibeAppSecretRefPathVarsSchema,
  DeleteVibeAppSecretRefResponseSchema,
  ListVibeAppCredentialBindingsPathVarsSchema,
  ListVibeAppCredentialBindingsResponseSchema,
  ListVibeAppSecretRefsPathVarsSchema,
  ListVibeAppSecretRefsResponseSchema,
  MaterializeVibeAppCredentialPathVarsSchema,
  MaterializeVibeAppCredentialResponseSchema,
  SetVibeAppSecretValueBodySchema,
  SetVibeAppSecretValuePathVarsSchema,
  SetVibeAppSecretValueResponseSchema,
  UnbindVibeAppCredentialPathVarsSchema,
  UnbindVibeAppCredentialResponseSchema,
  UpsertVibeAppSecretRefBodySchema,
  UpsertVibeAppSecretRefPathVarsSchema,
  UpsertVibeAppSecretRefResponseSchema
} from "../schemas";

export const VibeSecretEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeAppSecretRef — Vault pointers, NEVER secret values
  // ---------------------------------------------------------------------------

  /**
   * List secret refs for an app. Returns the row (id, name, scope,
   * vaultPath) but NEVER the value — reading values is a separate
   * Vault-only path. 404 if the app is not in the caller's org.
   */
  ListSecretRefs: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/secrets",
    PathVars: ListVibeAppSecretRefsPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeAppSecretRefsResponseSchema)
  },

  /**
   * Register a secret reference (`nexus app secret set NAME [--scope PROD]`).
   * Body has NO value field — this entity is the pointer. The actual
   * secret value gets written to Vault through a separate endpoint
   * (later slice) so the registry surface and the value-write surface
   * have independent access controls.
   *
   * Upserts on (vibeAppId, scope, name) — same set-or-update semantics
   * as env vars. The returned `vaultPath` is computed deterministically
   * by the backend so callers know where to push the value.
   */
  UpsertSecretRef: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/secrets",
    PathVars: UpsertVibeAppSecretRefPathVarsSchema,
    Body: UpsertVibeAppSecretRefBodySchema,
    Response: ApiSuccessSchema(UpsertVibeAppSecretRefResponseSchema)
  },

  /**
   * Set a secret's VALUE (`nexus app secret set NAME --value ...`). Unlike
   * UpsertSecretRef (the pointer), this carries the actual value: the control
   * plane KMS-encrypts it under the tenant's own recovery-seal CMK and parks
   * it as a pending write. The tenant's agent decrypts it on its next sync and
   * writes it into the cluster's Vault, then acks — the control plane can stage
   * but never read the value back. Staging a new value supersedes any prior
   * un-delivered one (latest wins). 404 if the ref is not in the caller's org.
   */
  SetSecretValue: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/secrets/:secretRefId/value",
    PathVars: SetVibeAppSecretValuePathVarsSchema,
    Body: SetVibeAppSecretValueBodySchema,
    Response: ApiSuccessSchema(SetVibeAppSecretValueResponseSchema)
  },

  /**
   * Remove a secret-ref pointer by id. Does NOT delete the underlying
   * Vault entry — that's a separate operator action to avoid making
   * a single API call cascade into a destructive Vault operation.
   * 404 if the row is not owned by the given app + org pair.
   */
  DeleteSecretRef: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:appId/secrets/:secretRefId",
    PathVars: DeleteVibeAppSecretRefPathVarsSchema,
    Response: ApiSuccessSchema(DeleteVibeAppSecretRefResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // VibeAppCredentialBinding — Nexus credentials bound BY REFERENCE
  // ---------------------------------------------------------------------------
  //
  // The answer to "an org already holds this credential; why is it being pasted
  // into an app env var again?". A binding entitles an app to one `Credential`
  // and names the env var its material lands on; the platform seals that
  // material under the tenant's own CMK and the agent delivers it to Vault. The
  // secret never leaves the credential store as plaintext, so central rotation
  // and revocation keep working.
  //
  // Built ON `VibeAppSecretRef`, never beside it: the pointer the binding mints
  // is an ordinary PLATFORM-owned secret ref, and materialization reuses the
  // same staging path a tenant-set value takes. There is one secrets mechanism
  // here, and this adds the missing REFERENCE to it.

  ListCredentialBindings: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/credential-bindings",
    PathVars: ListVibeAppCredentialBindingsPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeAppCredentialBindingsResponseSchema)
  },

  /**
   * Entitle an app to a credential. Idempotent: binding an already-bound
   * credential returns the existing binding with `created: false`.
   *
   * Does NOT seal the material — that is Materialize, separately, because
   * sealing has its own failure modes (the tenant's CMK may not be provisioned
   * yet) and a bind that fails for a reason unrelated to the credential is a
   * bind nobody can diagnose. It DOES refuse a credential that cannot be
   * resolved at all, so an unusable entitlement never gets written down.
   *
   * 400 unresolvable credential (only API-key credentials resolve today).
   * 404 app or credential not in the caller's org.
   * 409 a tenant-owned secret ref already occupies the env var name.
   */
  BindCredential: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/credential-bindings",
    PathVars: BindVibeAppCredentialPathVarsSchema,
    Body: BindVibeAppCredentialBodySchema,
    Response: ApiSuccessSchema(BindVibeAppCredentialResponseSchema)
  },

  /**
   * Seal the bound credential's current material and park it for the agent.
   * Safe to call on every deploy: when the app already holds exactly this
   * material the fingerprint matches, nothing stages, and `staged` is false.
   *
   * 400 material over the 4096-byte KMS Encrypt ceiling.
   * 404 binding not in the caller's org + app.
   * 409 the cluster has no CMK yet (retryable).
   */
  MaterializeCredentialBinding: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/credential-bindings/:bindingId/materialize",
    PathVars: MaterializeVibeAppCredentialPathVarsSchema,
    Response: ApiSuccessSchema(MaterializeVibeAppCredentialResponseSchema)
  },

  /**
   * Withdraw the entitlement. Soft delete — the row survives as the record of
   * what the app once read, and its platform secret ref survives with it so a
   * later re-bind reuses the pointer rather than colliding with it.
   *
   * Does NOT scrub the value already in the tenant's Vault: the control plane
   * holds `kms:Encrypt` and no `kms:Decrypt`, and has no delete path into a
   * cluster it cannot read. That is why deleting the CREDENTIAL refuses while a
   * live binding exists — unbinding first is the deliberate act that makes
   * revocation meaningful.
   */
  UnbindCredential: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:appId/credential-bindings/:bindingId",
    PathVars: UnbindVibeAppCredentialPathVarsSchema,
    Response: ApiSuccessSchema(UnbindVibeAppCredentialResponseSchema)
  }
} as const;
